import { IAdaptorService } from './../../../base/shared/syncfusion/adaptor-service.interface';
import { ToastService } from '../../../base/services/toast.service';
import {
  InfoPopupClient,
  InfoPopupDTO,
  CreateInfoPopupDTO,
  UpdateInfoPopupDTO,
  CreateInfoPopupTranslationDTO,
  UpdateInfoPopupTranslationDTO,
  FilterDTO,
  SortDirection,
  PagedResultDTOOfInfoPopupDTO,
  FilterOperator,
  InfoPopupPlatform,
  VersionComparisonType
} from '../../../base/models/models.swagger.generated';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { ErrorType } from 'src/app/core/routing/error-types';
import { ShowInactiveService } from 'src/app/base/shared/grid-base/show-inactive.service';
import { IPagedItems } from 'src/app/base/shared/syncfusion/paged-items.interface';

@Injectable()
export class PopupsAdministrationService extends ShowInactiveService implements IAdaptorService {

  constructor(
    private infoPopupClient: InfoPopupClient,
    private toastService: ToastService,
  ) {
    super();
  }

  public getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<IPagedItems> {
    if (!this.showInactive) {
      if (!filters) {
        filters = [];
      }
      filters.push(new FilterDTO({ isComplex: false, field: 'IsDeleted', operator: FilterOperator.Equal, value: 'false' }));
    }

    return this.infoPopupClient.searchInfoPopups(skip, take, sortBy, sortDirection, filters)
      .pipe(
        map(result => ({
          items: result?.items ?? [],
          totalNumberOfItems: result?.totalNumberOfItems ?? 0
        } as IPagedItems)),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  public addRecord(data: any): Observable<InfoPopupDTO> {
    const translations: CreateInfoPopupTranslationDTO[] = [];
    if (data.translations) {
      for (const t of data.translations) {
        if (t.title && t.title.trim()) {
          translations.push(new CreateInfoPopupTranslationDTO({
            language: t.language,
            title: t.title,
            body: t.body ?? '',
          }));
        }
      }
    }

    const dto = new CreateInfoPopupDTO({
      startDate: this.toDateTime(data.startDate),
      endDate: this.toDateTime(data.endDate),
      platform: Number(data.platform) as InfoPopupPlatform,
      versionComparison: Number(data.versionComparison) as VersionComparisonType,
      version: data.version ?? undefined,
      translations: translations,
    });

    return this.infoPopupClient.createInfoPopup(dto)
      .pipe(
        tap(() => {
          this.toastService.showSuccess('i18n.domain.popups.messages.add-title', 'i18n.domain.popups.messages.add-success-message');
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  public updateRecord(data: any): Observable<InfoPopupDTO> {
    const translations: UpdateInfoPopupTranslationDTO[] = [];
    if (data.translations) {
      for (const t of data.translations) {
        if (t.title && t.title.trim()) {
          translations.push(new UpdateInfoPopupTranslationDTO({
            language: t.language,
            title: t.title,
            body: t.body ?? '',
          }));
        }
      }
    }

    const dto = new UpdateInfoPopupDTO({
      id: data.id,
      startDate: this.toDateTime(data.startDate),
      endDate: this.toDateTime(data.endDate),
      platform: Number(data.platform) as InfoPopupPlatform,
      versionComparison: Number(data.versionComparison) as VersionComparisonType,
      version: data.version ?? undefined,
      translations: translations,
    });

    return this.infoPopupClient.updateInfoPopup(dto)
      .pipe(
        tap(() => {
          this.toastService.showSuccess('i18n.domain.popups.messages.update-title', 'i18n.domain.popups.messages.update-success-message');
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  public deleteRecord(id: number): Observable<any> {
    return this.infoPopupClient.deleteInfoPopup(id)
      .pipe(
        tap(() => {
          this.toastService.showSuccess('i18n.domain.popups.messages.delete-title', 'i18n.domain.popups.messages.delete-success-message');
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  private toDateTime(value: unknown): DateTime {
    if (value instanceof DateTime) {
      return value;
    }

    if (value instanceof Date) {
      return DateTime.fromJSDate(value);
    }

    if (typeof value === 'string') {
      return DateTime.fromISO(value);
    }

    return DateTime.invalid('Invalid input');
  }
}
